import { useCallback, useEffect, useState } from "react";
import {
  Stack,
  Title,
  Select,
  Button,
  Group,
  Loader,
  Text,
  Checkbox,
  Card,
  Paper,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import useTransientStore from "../../../data/transcientStore";
import { useTranslation } from "../../../hooks/useTranslation";
import {
  getUserDefaultSettings,
  updateUserDefaultSettings,
} from "../../../services/UserDefaultSettingsService";
import {
  UserDefaultSettingsData,
  UserDefaultSettingsRequest,
} from "../../../types/UserDefaultSettings";

interface UserDefaultSettingsPageProps {
  onBack?: () => void;
}

export default function UserDefaultSettingsPage({
  onBack,
}: Readonly<UserDefaultSettingsPageProps>) {
  const selectedAddressSystemId =
    useTransientStore((state) => state.selectedAddressSystem?.id) ?? 0;
  const { getTranslation } = useTranslation({
    pageId: 513, // Settings page
    addressSystemId: selectedAddressSystemId,
  });

  const [data, setData] = useState<UserDefaultSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [contractId, setContractId] = useState<string | null>(null);
  const [foremanId, setForemanId] = useState<string | null>(null);
  const [contractorForemanId, setContractorForemanId] = useState<
    string | null
  >(null);
  const [contractorContactId, setContractorContactId] = useState<
    string | null
  >(null);
  const [strengthId, setStrengthId] = useState<string | null>(null);
  const [subscriberId, setSubscriberId] = useState<string | null>(null);
  const [defaultMapArea, setDefaultMapArea] = useState<string | null>(null);
  const [selectedContracts, setSelectedContracts] = useState<number[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<number[]>([]);

  const t = useCallback(
    (key: string, fallback: string) => getTranslation(key) || fallback,
    [getTranslation],
  );

  useEffect(() => {
    const load = async () => {
      try {
        if (selectedAddressSystemId <= 0) {
          setLoading(false);
          return;
        }

        const result = await getUserDefaultSettings(selectedAddressSystemId);
        setData(result);

        // Initialize form state
        setContractId(
          result.settings.defaultContractId > 0
            ? result.settings.defaultContractId.toString()
            : null,
        );
        setForemanId(
          result.settings.defaultForemanId > 0
            ? result.settings.defaultForemanId.toString()
            : null,
        );
        setContractorForemanId(
          result.settings.defaultContractorForemanId > 0
            ? result.settings.defaultContractorForemanId.toString()
            : null,
        );
        setContractorContactId(
          result.settings.defaultContractorContactId > 0
            ? result.settings.defaultContractorContactId.toString()
            : null,
        );
        setStrengthId(
          result.settings.defaultStrengthId > 0
            ? result.settings.defaultStrengthId.toString()
            : null,
        );
        setSubscriberId(
          result.settings.defaultSubscriberId > 0
            ? result.settings.defaultSubscriberId.toString()
            : null,
        );
        setDefaultMapArea(
          result.settings.defaultMapArea > 0
            ? result.settings.defaultMapArea.toString()
            : null,
        );
        setSelectedContracts(result.settings.selectedContracts || []);
        setSelectedStatus(result.settings.selectedStatus || []);
      } catch (err) {
        console.error("Failed to load user default settings:", err);
        notifications.show({
          title: t("REPORTS_ERROR", "Error"),
          message: t(
            "USER_DEFAULTS_LOAD_ERROR",
            "Failed to load user default settings",
          ),
          color: "red",
        });
      } finally {
        setLoading(false);
      }
    };

    if (selectedAddressSystemId > 0) {
      load();
    }
  }, [selectedAddressSystemId, t]);

  const handleSave = async () => {
    try {
      setSaving(true);

      const request: UserDefaultSettingsRequest = {
        contractId: contractId ? parseInt(contractId) : 0,
        foremanId: foremanId ? parseInt(foremanId) : 0,
        contractorForemanId: contractorForemanId
          ? parseInt(contractorForemanId)
          : 0,
        contractorContactId: contractorContactId
          ? parseInt(contractorContactId)
          : 0,
        strengthId: strengthId ? parseInt(strengthId) : 0,
        subscriberId: subscriberId ? parseInt(subscriberId) : 0,
        defaultMapArea: defaultMapArea ? parseInt(defaultMapArea) : 0,
        contracts: selectedContracts,
        status: selectedStatus,
      };

      await updateUserDefaultSettings(selectedAddressSystemId, request);

      notifications.show({
        title: t("TEXT_TITLE_SUCCESS", "Success"),
        message: t(
          "USER_DEFAULTS_SAVE_SUCCESS",
          "User default settings saved successfully",
        ),
        color: "green",
      });
    } catch (err) {
      console.error("Failed to save user default settings:", err);
      notifications.show({
        title: t("REPORTS_ERROR", "Error"),
        message: t(
          "USER_DEFAULTS_SAVE_ERROR",
          "Failed to save user default settings",
        ),
        color: "red",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleContractCheckboxChange = (contractId: number) => {
    setSelectedContracts((prev) =>
      prev.includes(contractId)
        ? prev.filter((id) => id !== contractId)
        : [...prev, contractId],
    );
  };

  const handleStatusCheckboxChange = (statusId: number) => {
    setSelectedStatus((prev) =>
      prev.includes(statusId)
        ? prev.filter((id) => id !== statusId)
        : [...prev, statusId],
    );
  };

  const handleSelectAllContracts = () => {
    if (data) {
      setSelectedContracts(defaultContracts.map((c) => c.id));
    }
  };

  const handleDeselectAllContracts = () => {
    setSelectedContracts([]);
  };

  const handleSelectAllStatus = () => {
    if (data) {
      setSelectedStatus(data.statusOptions.map((s) => s.id));
    }
  };

  const handleDeselectAllStatus = () => {
    setSelectedStatus([]);
  };

  if (loading) {
    return (
      <Group justify="center" p="xl">
        <Loader />
      </Group>
    );
  }

  if (!data) {
    return (
      <Stack p="md">
        <Text c="dimmed">
          {t("USER_DEFAULTS_NO_DATA", "No data available")}
        </Text>
      </Stack>
    );
  }

  // Separate contracts into default and non-default for better organization
  const defaultContracts = data.contracts.filter((c) => c.isDefaultValue);

  return (
    <Stack h="100%" gap={0}>
      <Group justify="space-between" p="md" pb="sm">
        <Title order={3}>
          {t("TEXT_LABEL_OTSIKKO", "User Default Settings")}
        </Title>
        {onBack && (
          <Button variant="subtle" onClick={onBack}>
            {t("TEXT_LINK_PALAA", "Back")}
          </Button>
        )}
      </Group>

      <Paper
        withBorder
        radius="md"
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          margin: "0 16px",
        }}
      >
        <Card withBorder={false} p="md">
          <Stack gap="md">
          <Select
            label={t("TEXT_LABEL_OLETUS_SOPIMUS", "Default Contract")}
            placeholder={t("TEXT_LABEL_VALITSE", "Select")}
            data={data.contracts.map((c) => ({
              value: c.id.toString(),
              label: c.name,
            }))}
            value={contractId}
            onChange={setContractId}
            clearable
          />

          <Select
            label={t("TEXT_LABEL_OLETUS_VALVOJA", "Default Supervisor")}
            placeholder={t("TEXT_LABEL_VALITSE", "Select")}
            data={data.foremen.map((f) => ({
              value: f.id.toString(),
              label: f.name,
            }))}
            value={foremanId}
            onChange={setForemanId}
            clearable
          />

          <Select
            label={t("TEXT_LABEL_OLETUS_TYONJOHTAJA", "Default Foreman")}
            placeholder={t("TEXT_LABEL_VALITSE", "Select")}
            data={data.contractorForemen.map((f) => ({
              value: f.id.toString(),
              label: f.name,
            }))}
            value={contractorForemanId}
            onChange={setContractorForemanId}
            clearable
          />

          <Select
            label={t(
              "TEXT_LABEL_OLETUS_YHDYSHENKILO",
              "Default Contact Person",
            )}
            placeholder={t("TEXT_LABEL_VALITSE", "Select")}
            data={data.contractorContacts.map((c) => ({
              value: c.id.toString(),
              label: c.name,
            }))}
            value={contractorContactId}
            onChange={setContractorContactId}
            clearable
          />

          <Select
            label={t("TEXT_LABEL_OLETUS_TYYPPI", "Default Type")}
            placeholder={t("TEXT_LABEL_VALITSE", "Select")}
            data={data.strengths.map((s) => ({
              value: s.id.toString(),
              label: s.description,
            }))}
            value={strengthId}
            onChange={setStrengthId}
            clearable
          />

          <Select
            label={t("TEXT_LABEL_OLETUS_TILAAJA", "Default Subscriber")}
            placeholder={t("TEXT_LABEL_VALITSE", "Select")}
            data={data.subscribers.map((s) => ({
              value: s.id.toString(),
              label: s.name,
            }))}
            value={subscriberId}
            onChange={setSubscriberId}
            clearable
          />

          {data.mapAreas.length > 0 && (
            <Select
              label={t("TEXT_LABEL_OLETUS_ALUE", "Default Area")}
              placeholder={t("TEXT_LABEL_VALITSE", "Select")}
              data={data.mapAreas.map((a) => ({
                value: a.id.toString(),
                label: a.name,
              }))}
              value={defaultMapArea}
              onChange={setDefaultMapArea}
              clearable
            />
          )}

          {/* Only show checkboxes for contracts with default_value = 1 (matching PHP behavior) */}
          {defaultContracts.length > 0 && (
            <Paper withBorder radius="md" p="sm">
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text fw={500}>
                    {t("TEXT_LABEL_SOPIMUKSET", "Contracts")}
                  </Text>
                  <Group gap="xs">
                    <Button
                      size="xs"
                      variant="subtle"
                      onClick={handleSelectAllContracts}
                    >
                      {t("SETTINGS_SELECT_ALL", "Select All")}
                    </Button>
                    <Button
                      size="xs"
                      variant="subtle"
                      onClick={handleDeselectAllContracts}
                    >
                      {t("SETTINGS_DESELECT_ALL", "Deselect All")}
                    </Button>
                  </Group>
                </Group>
                {defaultContracts.map((contract) => (
                  <Checkbox
                    key={contract.id}
                    label={contract.name}
                    checked={selectedContracts.includes(contract.id)}
                    onChange={() => handleContractCheckboxChange(contract.id)}
                  />
                ))}
              </Stack>
            </Paper>
          )}

          {data.statusOptions.length > 0 && (
            <Paper withBorder radius="md" p="sm">
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text fw={500}>{t("TEXT_LABEL_TILAT", "Status")}</Text>
                  <Group gap="xs">
                    <Button
                      size="xs"
                      variant="subtle"
                      onClick={handleSelectAllStatus}
                    >
                      {t("SETTINGS_SELECT_ALL", "Select All")}
                    </Button>
                    <Button
                      size="xs"
                      variant="subtle"
                      onClick={handleDeselectAllStatus}
                    >
                      {t("SETTINGS_DESELECT_ALL", "Deselect All")}
                    </Button>
                  </Group>
                </Group>
                {data.statusOptions.map((status) => (
                  <Checkbox
                    key={status.id}
                    label={status.description}
                    checked={selectedStatus.includes(status.id)}
                    onChange={() => handleStatusCheckboxChange(status.id)}
                  />
                ))}
              </Stack>
            </Paper>
          )}
        </Stack>
      </Card>
    </Paper>

      <Group justify="flex-end" p="md" pt="sm">
        <Button onClick={handleSave} loading={saving}>
          {t("TEXT_BUTTON_LAHETA", "Save")}
        </Button>
      </Group>
    </Stack>
  );
}
